import api, { ApiResponse } from './index'
import { Message, Conversation, SendMessageData, CreateConversationData } from '@/types/message'

export const messageApi = {
    // Get all conversations for current user
    getConversations: (params: {
        page?: number
        limit?: number
    } = {}): Promise<ApiResponse<{
        conversations: Conversation[]
        totalCount: number
        currentPage: number
        totalPages: number
    }>> =>
        api.get('/messages/conversations', { params }),

    // Get single conversation by ID
    getConversationById: (id: string): Promise<ApiResponse<{ conversation: Conversation }>> =>
        api.get(`/messages/conversations/${id}`),

    // Create new conversation
    createConversation: (data: CreateConversationData): Promise<ApiResponse<{ conversation: Conversation }>> =>
        api.post('/messages/conversations', data),

    // Get messages for a conversation
    getMessages: (params: {
        conversationId: string
        page?: number
        limit?: number
        before?: string
    }): Promise<ApiResponse<{
        messages: Message[]
        hasMore: boolean
        isInitialLoad: boolean
        totalCount: number
    }>> =>
        api.get('/messages', { params }),

    // Send a message
    sendMessage: (data: SendMessageData): Promise<ApiResponse<{ message: Message }>> =>
        api.post('/messages', data),

    // Send message with file upload
    sendMessageWithFiles: (formData: FormData): Promise<ApiResponse<{ message: Message }>> =>
        api.post('/messages/upload', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }),

    // Mark messages as read
    markMessagesAsRead: (conversationId: string): Promise<ApiResponse<{ count: number }>> =>
        api.post(`/messages/conversations/${conversationId}/read`),

    // Delete a message
    deleteMessage: (messageId: string): Promise<ApiResponse> =>
        api.delete(`/messages/${messageId}`),

    // Edit a message
    editMessage: (messageId: string, content: string): Promise<ApiResponse<{ message: Message }>> =>
        api.put(`/messages/${messageId}`, { content }),

    // Search messages
    searchMessages: (params: {
        query: string
        conversationId?: string
        limit?: number
    }): Promise<ApiResponse<{
        messages: Message[]
        conversations: Conversation[]
    }>> =>
        api.get('/messages/search', { params }),

    // Get unread messages count
    getUnreadCount: (): Promise<ApiResponse<{ count: number }>> =>
        api.get('/messages/unread-count'),

    // Archive conversation
    archiveConversation: (conversationId: string): Promise<ApiResponse<{ conversation: Conversation }>> =>
        api.post(`/messages/conversations/${conversationId}/archive`),

    // Unarchive conversation
    unarchiveConversation: (conversationId: string): Promise<ApiResponse<{ conversation: Conversation }>> =>
        api.post(`/messages/conversations/${conversationId}/unarchive`),

    // Delete conversation
    deleteConversation: (conversationId: string): Promise<ApiResponse> =>
        api.delete(`/messages/conversations/${conversationId}`),

    // Mute conversation
    muteConversation: (conversationId: string, duration?: number): Promise<ApiResponse<{ conversation: Conversation }>> =>
        api.post(`/messages/conversations/${conversationId}/mute`, { duration }),

    // Unmute conversation
    unmuteConversation: (conversationId: string): Promise<ApiResponse<{ conversation: Conversation }>> =>
        api.post(`/messages/conversations/${conversationId}/unmute`),

    // Report conversation or message
    reportMessage: (messageId: string, reason: string, description?: string): Promise<ApiResponse> =>
        api.post(`/messages/${messageId}/report`, { reason, description }),

    // Block user
    blockUser: (userId: string): Promise<ApiResponse> =>
        api.post(`/messages/block/${userId}`),

    // Unblock user
    unblockUser: (userId: string): Promise<ApiResponse> =>
        api.post(`/messages/unblock/${userId}`),

    // Get blocked users
    getBlockedUsers: (): Promise<ApiResponse<{ users: any[] }>> =>
        api.get('/messages/blocked-users'),

    // Get conversation participants
    getConversationParticipants: (conversationId: string): Promise<ApiResponse<{ participants: any[] }>> =>
        api.get(`/messages/conversations/${conversationId}/participants`),

    // Add participant to conversation (future group chat feature)
    addParticipant: (conversationId: string, userId: string): Promise<ApiResponse<{ conversation: Conversation }>> =>
        api.post(`/messages/conversations/${conversationId}/participants`, { userId }),

    // Remove participant from conversation
    removeParticipant: (conversationId: string, userId: string): Promise<ApiResponse<{ conversation: Conversation }>> =>
        api.delete(`/messages/conversations/${conversationId}/participants/${userId}`),

    // Update conversation settings
    updateConversationSettings: (
        conversationId: string,
        settings: {
            name?: string
            description?: string
            theme?: string
        }
    ): Promise<ApiResponse<{ conversation: Conversation }>> =>
        api.put(`/messages/conversations/${conversationId}/settings`, settings),

    // Get message delivery status
    getMessageStatus: (messageId: string): Promise<ApiResponse<{
        delivered: boolean
        read: boolean
        deliveredAt?: string
        readAt?: string
    }>> =>
        api.get(`/messages/${messageId}/status`),

    // Forward message
    forwardMessage: (
        messageId: string,
        conversationIds: string[]
    ): Promise<ApiResponse<{ messages: Message[] }>> =>
        api.post(`/messages/${messageId}/forward`, { conversationIds }),

    // Pin message
    pinMessage: (messageId: string): Promise<ApiResponse<{ message: Message }>> =>
        api.post(`/messages/${messageId}/pin`),

    // Unpin message
    unpinMessage: (messageId: string): Promise<ApiResponse<{ message: Message }>> =>
        api.post(`/messages/${messageId}/unpin`),

    // Get pinned messages for conversation
    getPinnedMessages: (conversationId: string): Promise<ApiResponse<{ messages: Message[] }>> =>
        api.get(`/messages/conversations/${conversationId}/pinned`),
}